import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { aFechaISO, formatearFecha } from '@/lib/fecha'
import { useClaveIdempotencia } from '@/lib/idempotencia'
import { formatearRNC } from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, EntradaTexto } from '@/ui/CampoFormulario'
import { Esqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, FechaHora, Monto } from '@/ui/Numero'
import { DialogoConMotivo } from '@/consola/DialogoConMotivo'
import { Decimal, sumar } from '@/dominio/decimal'
import { traducirErrorConsola } from '@/dominio/consola/useConsola'
import { PRESENTACION_CARGO, PRESENTACION_ETAPA, monedaPresentable, nombrePeriodo } from '@/dominio/cuenta/tipos'
import { PRESENTACION_VINCULO, type CargoDespacho, type FichaDespachoConsola } from '@/dominio/despacho/tipos'
import {
  useAnularCargoDespacho,
  useAuditoriaDespachoConsola,
  useComprobanteDespacho,
  useEstadoDespacho,
  useFichaDespacho,
  useGenerarCargoDespacho,
  useRegistrarPagoDespacho,
  useTarifaDespacho,
  useVerificarDespacho,
} from '@/dominio/despacho/useDespachosConsola'
import { Dato, EncabezadoPantalla, Tarjeta } from '../../-pantalla'
import { DetalleJson, ErrorDeCarga, SoloDueno, entero, useEsDueno } from '../-comun'

/**
 * La ficha de un despacho en la consola (`docs/09` §5.9): quién es, su equipo,
 * sus vínculos con los clientes —todos, con el contribuyente nombrado: la
 * consola ya los ve a todos—, su cuenta con Agentical Group y su auditoría.
 *
 * **Suspender** corta el acceso de todo el equipo a todos sus clientes sin
 * tocar los vínculos: al reactivar, vuelve como estaba. **Cerrar** es final:
 * termina los vínculos y lo anota en la auditoría de cada cliente.
 */
export const Route = createFileRoute('/consola/despachos/$id')({
  component: FichaDespacho,
})

type Dialogo =
  | { tipo: 'estado'; estado: 'activo' | 'suspendido' | 'cerrado' }
  | { tipo: 'verificar' }
  | { tipo: 'tarifa' }
  | { tipo: 'generar' }
  | { tipo: 'pago' }
  | { tipo: 'anular'; cargo: CargoDespacho }

const th = 'h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600'

function FichaDespacho() {
  const { id } = Route.useParams()
  const dueno = useEsDueno()
  const consulta = useFichaDespacho(id)
  const auditoria = useAuditoriaDespachoConsola(id)
  const comprobante = useComprobanteDespacho(id)
  const [dialogo, setDialogo] = useState<Dialogo | null>(null)
  const cerrar = () => setDialogo(null)

  if (consulta.isError) {
    return <ErrorDeCarga error={consulta.error} queCargaba="el despacho" onReintentar={() => void consulta.refetch()} />
  }

  const ficha = consulta.data
  if (ficha === undefined) return <Esqueleto className="h-64 w-full" />

  const d = ficha.despacho
  const etapa = PRESENTACION_ETAPA[ficha.cuenta.estado.etapa]
  const porCobrar = ficha.cuenta.cargos.filter((c) => c.estado === 'pendiente' || c.estado === 'fallido')

  return (
    <div className="flex flex-col gap-6">
      <Link to="/consola/despachos" className="flex w-fit items-center gap-1 text-etiqueta text-gris-600 hover:text-gris-900">
        <ArrowLeft aria-hidden="true" className="size-4" /> Despachos
      </Link>

      <EncabezadoPantalla
        titulo={d.nombre}
        descripcion={`${d.tipoIdentificacion === 'rnc' ? 'RNC' : 'Cédula'} ${formatearRNC(d.identificacion)} · ${d.correo} · alta ${
          d.origen === 'consola' ? 'desde la consola' : 'desde el portal'
        } el ${formatearFecha(d.creadoEl)}`}
        acciones={
          dueno ? (
            d.estado === 'cerrado' ? null : (
              <>
                {d.verificadoEl === null ? (
                  <Boton onClick={() => setDialogo({ tipo: 'verificar' })}>Verificar</Boton>
                ) : null}
                {d.estado === 'activo' ? (
                  <Boton onClick={() => setDialogo({ tipo: 'estado', estado: 'suspendido' })}>Suspender</Boton>
                ) : (
                  <Boton onClick={() => setDialogo({ tipo: 'estado', estado: 'activo' })}>Reactivar</Boton>
                )}
                <Boton variante="destructivo" onClick={() => setDialogo({ tipo: 'estado', estado: 'cerrado' })}>
                  Cerrar
                </Boton>
              </>
            )
          ) : (
            <SoloDueno />
          )
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta titulo="El despacho">
          <dl>
            <Dato etiqueta="Estado">
              <InsigniaEstado tono={d.estado === 'activo' ? 'verde' : d.estado === 'suspendido' ? 'ambar' : 'neutral'}>
                {d.estado}
              </InsigniaEstado>
            </Dato>
            <Dato etiqueta="Verificado">
              {d.verificadoEl ? `el ${formatearFecha(d.verificadoEl)} por ${d.verificadoPor ?? '—'}` : 'no'}
            </Dato>
            <Dato etiqueta="Creado por">{d.creadoPor ?? '—'}</Dato>
            {d.motivoEstado ? <Dato etiqueta="Motivo del estado">{d.motivoEstado}</Dato> : null}
          </dl>
        </Tarjeta>

        <Tarjeta
          titulo="Cuenta con Agentical Group"
          acciones={
            dueno && d.estado !== 'cerrado' ? (
              <span className="flex gap-2">
                <Boton tamano="menor" onClick={() => setDialogo({ tipo: 'tarifa' })}>
                  Tarifa
                </Boton>
                <Boton tamano="menor" onClick={() => setDialogo({ tipo: 'generar' })}>
                  Generar cargo
                </Boton>
                <Boton tamano="menor" variante="primario" disabled={porCobrar.length === 0} onClick={() => setDialogo({ tipo: 'pago' })}>
                  Registrar pago
                </Boton>
              </span>
            ) : null
          }
        >
          <dl>
            <Dato etiqueta="Etapa">
              <InsigniaEstado tono={etapa.tono}>{etapa.etiqueta}</InsigniaEstado>
            </Dato>
            <Dato etiqueta="Saldo">
              <Monto valor={ficha.cuenta.estado.saldo} moneda={monedaPresentable(ficha.cuenta.estado.moneda)} mostrarMoneda />
            </Dato>
            <Dato etiqueta="Tarifa mensual">
              <Monto valor={ficha.cuenta.tarifa.precioMensual} moneda={monedaPresentable(ficha.cuenta.tarifa.moneda)} mostrarMoneda />
              {ficha.cuenta.tarifa.activa ? '' : ' · inactiva'}
            </Dato>
          </dl>
          <p className="mt-2 text-etiqueta text-gris-600">{ficha.cuenta.estado.mensaje}</p>
          {!ficha.emisorConfigurado ? (
            <p className="mt-1 text-etiqueta text-ambar">
              Sin Cobros__TenantEmisor: los pagos quedan con el comprobante pendiente.
            </p>
          ) : null}
        </Tarjeta>
      </div>

      <Equipo ficha={ficha} />
      <Vinculos ficha={ficha} />

      <Tarjeta titulo="Cargos y pagos" relleno={false}>
        {ficha.cuenta.cargos.length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Sin cargos. Con la tarifa en cero no se genera ninguno." />
        ) : (
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className={th}>Nº</th>
                <th scope="col" className={th}>Período</th>
                <th scope="col" className={th}>Vence</th>
                <th scope="col" className={`${th} text-right`}>Total</th>
                <th scope="col" className={th}>Estado</th>
                <th scope="col" className={th}>Comprobante</th>
                <th scope="col" className={th}>
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {ficha.cuenta.cargos.map((c) => {
                const pago = ficha.cuenta.pagos.find((p) => p.id === c.pagoId)
                return (
                  <tr key={c.id} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                    <td className="numero px-2">{c.numero}</td>
                    <td className="px-2">{nombrePeriodo(c.periodo.slice(0, 7))}</td>
                    <td className="numero px-2">{formatearFecha(c.venceEl)}</td>
                    <td className="px-2 text-right">
                      <Monto valor={c.total} moneda={monedaPresentable(c.moneda)} />
                    </td>
                    <td className="px-2">
                      <InsigniaEstado tono={PRESENTACION_CARGO[c.estado].tono}>{PRESENTACION_CARGO[c.estado].etiqueta}</InsigniaEstado>
                    </td>
                    <td className="px-2 text-etiqueta">
                      {pago?.comprobanteENCF ? (
                        <ENCF valor={pago.comprobanteENCF} />
                      ) : pago ? (
                        <span className="flex items-center gap-2">
                          <span className="text-gris-600">{pago.comprobanteDetalle ?? 'pendiente'}</span>
                          {dueno ? (
                            <Boton tamano="menor" disabled={comprobante.isPending} onClick={() => comprobante.mutate(pago.id)}>
                              Emitir
                            </Boton>
                          ) : null}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-2">
                      {dueno && (c.estado === 'pendiente' || c.estado === 'fallido') ? (
                        <Boton tamano="menor" variante="discreto" onClick={() => setDialogo({ tipo: 'anular', cargo: c })}>
                          Anular
                        </Boton>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Tarjeta>

      <Tarjeta titulo="Auditoría del despacho" relleno={false}>
        {auditoria.isError ? (
          <ErrorDeCarga error={auditoria.error} queCargaba="la auditoría" onReintentar={() => void auditoria.refetch()} />
        ) : (auditoria.data ?? []).length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Nada anotado todavía." />
        ) : (
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className={`${th} w-[170px]`}>Cuándo</th>
                <th scope="col" className={th}>Acción</th>
                <th scope="col" className={th}>Quién</th>
                <th scope="col" className={th}>Cliente</th>
                <th scope="col" className={th}>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {(auditoria.data ?? []).map((e) => (
                <tr key={e.id} className="border-b border-gris-200 align-top last:border-b-0">
                  <td className="px-2 py-1">
                    <FechaHora valor={e.ocurridoEl} />
                  </td>
                  <td className="mono px-2 py-1 text-etiqueta">{e.accion}</td>
                  <td className="px-2 py-1">{e.operador ? `${e.email ?? '—'} (consola)` : (e.email ?? '—')}</td>
                  <td className="px-2 py-1">{e.razonSocial ?? '—'}</td>
                  <td className="max-w-[320px] px-2 py-1">
                    <DetalleJson valor={e.detalle} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {dialogo?.tipo === 'estado' ? <DialogoEstado id={id} estado={dialogo.estado} nombre={d.nombre} onCerrar={cerrar} /> : null}
      {dialogo?.tipo === 'verificar' ? <DialogoVerificar id={id} onCerrar={cerrar} /> : null}
      {dialogo?.tipo === 'tarifa' ? (
        <DialogoTarifa id={id} actual={ficha.cuenta.tarifa.precioMensual} activa={ficha.cuenta.tarifa.activa} onCerrar={cerrar} />
      ) : null}
      {dialogo?.tipo === 'generar' ? <DialogoGenerar id={id} onCerrar={cerrar} /> : null}
      {dialogo?.tipo === 'pago' ? <DialogoPago id={id} nombre={d.nombre} cargos={porCobrar} onCerrar={cerrar} /> : null}
      {dialogo?.tipo === 'anular' ? <DialogoAnular id={id} cargo={dialogo.cargo} onCerrar={cerrar} /> : null}
    </div>
  )
}

function Equipo({ ficha }: { ficha: FichaDespachoConsola }) {
  return (
    <Tarjeta titulo={`Equipo (${ficha.miembros.filter((m) => m.retiradoEl === null).length})`} relleno={false}>
      <table className="w-full border-collapse text-cuerpo">
        <thead className="bg-gris-50">
          <tr className="border-b border-gris-300">
            <th scope="col" className={th}>Persona</th>
            <th scope="col" className={th}>Rol</th>
            <th scope="col" className={th}>Cuenta</th>
            <th scope="col" className={`${th} text-right`}>Asignados</th>
            <th scope="col" className={th}>Desde</th>
          </tr>
        </thead>
        <tbody>
          {ficha.miembros.map((m) => (
            <tr key={m.miembroId} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
              <td className="px-2">{m.email}</td>
              <td className="px-2">{m.rol}</td>
              <td className="px-2">{m.retiradoEl ? `retirado el ${formatearFecha(m.retiradoEl)}` : m.estadoCuenta}</td>
              <td className="numero px-2 text-right">{m.rol === 'titular' ? 'todos' : entero(m.clientesAsignados)}</td>
              <td className="numero px-2">{formatearFecha(m.activoDesde)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Tarjeta>
  )
}

function Vinculos({ ficha }: { ficha: FichaDespachoConsola }) {
  return (
    <Tarjeta titulo={`Vínculos (${ficha.vinculos.filter((v) => v.estado === 'aceptado').length} vigentes)`} relleno={false}>
      {ficha.vinculos.length === 0 ? (
        <EstadoVacio variante="primera-vez" mensaje="Sin vínculos." />
      ) : (
        <table className="w-full border-collapse text-cuerpo">
          <thead className="bg-gris-50">
            <tr className="border-b border-gris-300">
              <th scope="col" className={th}>Cliente</th>
              <th scope="col" className={th}>Estado</th>
              <th scope="col" className={th}>Propuso</th>
              <th scope="col" className={th}>Desde / hasta</th>
              <th scope="col" className={th}>Lo ven, además de los titulares</th>
            </tr>
          </thead>
          <tbody>
            {ficha.vinculos.map((v) => (
              <tr key={v.id} className="h-[var(--alto-fila)] border-b border-gris-200 align-top last:border-b-0">
                <td className="px-2 py-1">
                  {v.tenant ? (
                    <Link to="/consola/clientes/$id" params={{ id: v.tenant }} className="flex flex-col hover:text-marca">
                      <span>{v.razonSocial ?? '—'}</span>
                      <span className="numero text-etiqueta text-gris-600">RNC {formatearRNC(v.rncCliente)}</span>
                    </Link>
                  ) : (
                    <span className="numero">RNC {formatearRNC(v.rncCliente)}</span>
                  )}
                </td>
                <td className="px-2 py-1">
                  <InsigniaEstado tono={PRESENTACION_VINCULO[v.estado].tono}>{PRESENTACION_VINCULO[v.estado].etiqueta}</InsigniaEstado>
                </td>
                <td className="px-2 py-1">{v.iniciadoPor === 'despacho' ? 'el despacho' : 'el cliente'}</td>
                <td className="numero px-2 py-1 text-etiqueta">
                  {formatearFecha(v.respondidoEl ?? v.propuestoEl)}
                  {v.terminadoEl ? ` – ${formatearFecha(v.terminadoEl)} (${v.terminadoLado ?? ''})` : ''}
                </td>
                <td className="px-2 py-1 text-etiqueta">{v.asignados.length > 0 ? v.asignados.join(', ') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Tarjeta>
  )
}

function DialogoEstado({
  id,
  estado,
  nombre,
  onCerrar,
}: {
  id: string
  estado: 'activo' | 'suspendido' | 'cerrado'
  nombre: string
  onCerrar: () => void
}) {
  const cambiar = useEstadoDespacho(id)
  const textos = {
    activo: { titulo: 'Reactivar el despacho', boton: 'Reactivar', texto: 'Su equipo vuelve a entrar a la cartera y a sus clientes, con los vínculos que tenía.' },
    suspendido: { titulo: 'Suspender el despacho', boton: 'Suspender', texto: 'Nadie del equipo entra a ningún cliente ni ve la cartera hasta que se reactive. Los vínculos no se tocan.' },
    cerrado: { titulo: 'Cerrar el despacho', boton: 'Cerrar', texto: 'Es final: se terminan todos sus vínculos, cada cliente lo ve en su auditoría, y el despacho no se reabre.' },
  }[estado]

  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => cambiar.mutate({ estado, motivo }, { onSuccess: onCerrar })}
      titulo={textos.titulo}
      descripcion={<p>{textos.texto}</p>}
      etiquetaConfirmar={textos.boton}
      destructiva={estado !== 'activo'}
      textoAEscribir={estado === 'cerrado' ? nombre : undefined}
      trabajando={cambiar.isPending}
      error={cambiar.isError ? traducirErrorConsola(cambiar.error, 'cambiar el estado') : null}
    />
  )
}

function DialogoVerificar({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const verificar = useVerificarDespacho(id)
  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => verificar.mutate(motivo, { onSuccess: onCerrar })}
      titulo="Verificar el despacho"
      descripcion={<p>Sus clientes lo verán como verificado por Agentical Group en las invitaciones. Diga cómo se comprobó el RNC.</p>}
      etiquetaConfirmar="Verificar"
      trabajando={verificar.isPending}
      error={verificar.isError ? traducirErrorConsola(verificar.error, 'verificar el despacho') : null}
    />
  )
}

function DialogoTarifa({ id, actual, activa, onCerrar }: { id: string; actual: string; activa: boolean; onCerrar: () => void }) {
  const fijar = useTarifaDespacho(id)
  const [precio, setPrecio] = useState(actual)
  const [estaActiva, setActiva] = useState(activa || actual === '0.00')
  const valido = /^\d+(\.\d{1,2})?$/.test(precio.trim())

  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) =>
        fijar.mutate({ precioMensual: Decimal.desde(precio.trim()).aTexto(), activa: estaActiva, motivo }, { onSuccess: onCerrar })
      }
      titulo="Tarifa del despacho"
      descripcion={
        <p>
          El precio mensual de la cartera, antes de ITBIS. En cero no se cobra nada: es el valor mientras no se decida otro.
          Se cobra desde el cargo del mes siguiente que se genere.
        </p>
      }
      etiquetaConfirmar="Guardar"
      listo={valido}
      trabajando={fijar.isPending}
      error={fijar.isError ? traducirErrorConsola(fijar.error, 'fijar la tarifa') : null}
    >
      <CampoFormulario etiqueta="Precio mensual (DOP)" obligatorio>
        {(atributos) => <EntradaTexto {...atributos} value={precio} inputMode="decimal" onChange={(e) => setPrecio(e.target.value)} />}
      </CampoFormulario>
      <label className="flex items-center gap-2 text-cuerpo">
        <input type="checkbox" checked={estaActiva} onChange={(e) => setActiva(e.target.checked)} />
        Se cobra
      </label>
    </DialogoConMotivo>
  )
}

function DialogoGenerar({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const generar = useGenerarCargoDespacho(id)
  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => generar.mutate(motivo, { onSuccess: onCerrar })}
      titulo="Generar el cargo del mes"
      descripcion={<p>Lo mismo que hace el proceso diario: si el cargo del mes ya existe —o se anuló— no se crea otro.</p>}
      etiquetaConfirmar="Generar"
      trabajando={generar.isPending}
      error={generar.isError ? traducirErrorConsola(generar.error, 'generar el cargo') : null}
    />
  )
}

function DialogoPago({ id, nombre, cargos, onCerrar }: { id: string; nombre: string; cargos: CargoDespacho[]; onCerrar: () => void }) {
  // El recibo: una clave por diálogo, nunca por render.
  const recibo = useClaveIdempotencia()
  const registrar = useRegistrarPagoDespacho(id)
  const [elegidos, setElegidos] = useState<Set<string>>(() => new Set(cargos.map((c) => c.id)))
  const [referencia, setReferencia] = useState('')
  const [pagadoEl, setPagadoEl] = useState(() => aFechaISO(new Date()))

  const seleccionados = cargos.filter((c) => elegidos.has(c.id))
  const total = sumar(seleccionados.map((c) => Decimal.desde(c.total)))
  const listo = seleccionados.length > 0 && referencia.trim().length >= 3 && pagadoEl !== ''

  const alternar = (cargo: string) => {
    const siguiente = new Set(elegidos)
    if (siguiente.has(cargo)) siguiente.delete(cargo)
    else siguiente.add(cargo)
    setElegidos(siguiente)
  }

  return (
    <DialogoConMotivo
      abierto
      ancho="ancho"
      onCancelar={onCerrar}
      onConfirmar={(motivo) =>
        registrar.mutate(
          { recibo, cargos: seleccionados.map((c) => c.id), referencia: referencia.trim(), pagadoEl, monto: total.aTexto(), motivo },
          { onSuccess: onCerrar },
        )
      }
      titulo="Registrar un pago por transferencia"
      descripcion={
        <p>
          Salda los cargos marcados de {nombre}, completos. Se emite el e-CF de Agentical Group por cada uno; si no se puede, el
          pago queda con el comprobante pendiente.
        </p>
      }
      etiquetaConfirmar={`Registrar ${total.aTexto()}`}
      trabajando={registrar.isPending}
      listo={listo}
      error={registrar.isError ? traducirErrorConsola(registrar.error, 'registrar el pago') : null}
    >
      <fieldset className="flex flex-col gap-1">
        <legend className="text-etiqueta font-medium text-gris-600">Cargos que salda</legend>
        {cargos.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-cuerpo">
            <input type="checkbox" checked={elegidos.has(c.id)} onChange={() => alternar(c.id)} />
            <span className="numero text-gris-600">Nº {c.numero}</span>
            <span className="flex-1">{nombrePeriodo(c.periodo.slice(0, 7))}</span>
            <Monto valor={c.total} moneda={monedaPresentable(c.moneda)} />
          </label>
        ))}
      </fieldset>
      <div className="grid gap-3 md:grid-cols-2">
        <CampoFormulario etiqueta="Referencia de la transferencia" obligatorio>
          {(atributos) => (
            <EntradaTexto {...atributos} value={referencia} maxLength={120} onChange={(e) => setReferencia(e.target.value)} />
          )}
        </CampoFormulario>
        <CampoFormulario etiqueta="Acreditada el" obligatorio>
          {(atributos) => <EntradaTexto {...atributos} type="date" value={pagadoEl} onChange={(e) => setPagadoEl(e.target.value)} />}
        </CampoFormulario>
      </div>
    </DialogoConMotivo>
  )
}

function DialogoAnular({ id, cargo, onCerrar }: { id: string; cargo: CargoDespacho; onCerrar: () => void }) {
  const anular = useAnularCargoDespacho(id)
  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => anular.mutate({ cargo: cargo.id, motivo }, { onSuccess: onCerrar })}
      titulo={`Anular el cargo ${cargo.numero}`}
      descripcion={<p>Para lo que no se debía cobrar. Lo pagado no se anula: se revierte con una nota de crédito.</p>}
      etiquetaConfirmar="Anular"
      destructiva
      trabajando={anular.isPending}
      error={anular.isError ? traducirErrorConsola(anular.error, 'anular el cargo') : null}
    />
  )
}
